<template>
    <tailwind-layout title="Maintenance Request Types">
        <template #header>
            <div class="px-4 sm:px-6 lg:px-8 mx-auto hidden lg:flex justify-between items-center"><!-- Block for lg and larger -->
                <div class="flex flex-col">
                    <h1 class="font-semibold text-2xl leading-tight">Maintenance Request Types</h1>
                    <h2 class="text-xs">Add Request Types to begin managing maintenence properties.</h2>
                </div>
                <div class="flex items-center space-x-4">
                    <inertia-link :href="route('maintenance.index')"
                        class="mt-1 relative inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10">
                        <font-awesome-icon :icon="faChevronLeft" class="h-5 w-5 text-gray-400" aria-hidden="true" /> Back
                    </inertia-link>
                    <button @click="openAddModal" class="primaryButton">
                        Add Request Option
                    </button>
                </div>
            </div>

            <div class="mb-3 px-6 lg:px-8 mx-auto flex flex-col lg:hidden"><!-- Block for sm, md, lg (buttons will stack on smaller screens) -->
                <div class="flex justify-between items-start md:items-center">
                    <div class="flex flex-col">
                        <h1 class="font-semibold text-2xl leading-tight">Maintenance Request Types</h1>
                        <h2 class="text-xs">Add Request Types to begin managing maintenence properties.</h2>
                    </div>
                    <button @click="openAddModal" class="primaryButton">
                        Add Request Option
                    </button>
                </div>
            </div>
        </template>
        <div class="overflow-hidden">
            <div class="relative">
                <div class="grid grid-cols-3 sm:grid-cols-2 gap-4 mb-6 sm:flex sm:justify-items-stretch ml-2 lg:ml-0">
                    <div class="w-full sm:w-2/12 pr-4 order-2 sm:order-1">
                        <select v-model="form.paginate" @change="fetchData"
                            class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-100 drop-shadow focus:outline-none focus:ring-indigo-400 focus:border-indigo-400 sm:text-sm rounded-md">
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                        </select>
                    </div>
                    <div class="col-span-3 sm:w-6/12 sm:pr-4 order-1 sm:order-2 px-2">
                        <input id="search" v-model="form.search" type="text" placeholder="Search Emergency Options..." class="mt-1 shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-100 drop-shadow rounded-md sm:mx-2">
                        <div class="flex items-center mt-2 md:ml-2" @click="show_archived_options()">
                            <SwitchGroup as="div" class="flex items-center" v-model="form.trashed">
                                <Switch :class="[form.trashed ? 'bg-rent-risk-blue-400' : 'bg-gray-200', 'relative inline-flex h-4 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-rent-risk-blue-400 focus:ring-offset-2']">
                                    <span aria-hidden="true" :class="[form.trashed ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none inline-block size-3 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out']" />
                                </Switch>
                                <SwitchLabel as="span" class="ml-3 text-sm">
                                    <span class="font-medium text-gray-700">Show Archived Options</span>
                                </SwitchLabel>
                            </SwitchGroup>
                        </div>
                    </div>
                </div>
            </div>
            <div class="rounded-md lg:shadow shadow-none overflow-x-auto">
                <div class="flex flex-col">
                    <div class="overflow-x-auto">
                        <div class="align-middle inline-block min-w-full">
                            <div class="shadow overflow-hidden lg:border">
                                <input ref="sort_by" type="hidden" v-model="form.sort_by" />
                                <input ref="sort_direction" type="hidden" v-model="form.sort_direction" />
                                <table class="min-w-full divide-y divide-gray-300 hidden lg:table">
                                    <thead class="bg-blue-50">
                                        <tr>
                                            <th scope="col" class="px-6 py-3 text-sm font-semibold text-gray-900 text-left">
                                                #
                                            </th>
                                            <th scope="col" class="px-6 py-3 text-sm font-semibold text-gray-900 text-left">
                                                Name
                                            </th>
                                            <th scope="col" class="px-6 py-3 text-sm font-semibold text-gray-900 text-left">
                                                Description
                                            </th>
                                            <th scope="col" class="px-6 py-3 text-sm font-semibold text-gray-900 text-left">
                                                Type
                                            </th>
                                            <th scope="col" class="px-6 py-3 text-sm font-semibold text-gray-900 text-right">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody class="divide-y divide-gray-200">
                                        <tr v-for="(type, index) in userEmergencyTypes.data" :key="index"
                                           :class="[(index % 2 === 0 && !type.deleted_at ? 'bg-gray-50 border-b hover:bg-yellow-50 hover:cursor-pointer' : (!type.deleted_at) ? 'bg-white border-b hover:bg-yellow-50 hover:cursor-pointer' : ''),
                                        (type.deleted_at ? 'bg-red-100 !border-b !border-red-200 hover:bg-red-200 hover:cursor-pointer' : '')]"
                                    >
                                            <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {{ type.id }}
                                            </td>
                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {{ type.name }}
                                            </td>
                                            <td class="px-6 py-4 whitespace-normal text-sm text-gray-500 truncate" style="max-width: 200px">
                                                {{ type.description || 'No description' }}
                                            </td>
                                            <td class="px-6 py-6 whitespace-nowrap text-xs text-gray-500 uppercase">
                                                <span class="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-md bg-rent-risk-blue-400 text-white">
                                                    <span v-if="type.type === 'non_emergency'">Non Emergency</span>
                                                    <span v-else-if="type.type === 'emergency'">Emergency</span>
                                                </span>
                                            </td>
                                            <td class="px-6 py-4 whitespace-nowrap text-sm text-right">
                                                <button v-if="type.deleted_at" @click="openRestoreModal(type)"
                                                    class="bg-blue-50 px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-gray-300 hover:bg-blue-200 rounded-md">
                                                    Restore
                                                </button>
                                                <button v-if="!type.deleted_at" @click="openEditModal(type)"  class="relative -ml-px inline-flex items-center bg-blue-50 px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-blue-200 focus:z-10">
                                                    Edit
                                                </button>
                                                <button v-if="!type.deleted_at" @click="openDeleteModal(type)"  class="relative -ml-px inline-flex items-center bg-blue-50 px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-blue-200 focus:z-10">
                                                    Archive
                                                </button>

                                            </td>
                                        </tr>
                                        <tr v-if="userEmergencyTypes.data.length === 0">
                                            <td colspan="2" class="px-6 py-4 whitespace-nowrap text-gray-400 text-sm font-medium text-center">
                                                No Emergency Options found.
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>

                                <!-- Stacked layout for sm, md, and lg (table becomes vertical) -->
                                <ul role="list" class="lg:hidden grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 my-5 px-2">
                                    <li v-for="(type, index) in userEmergencyTypes.data" :key="index" :class="[type.deleted_at ? 'bg-red-100 hover:bg-red-50 hover:outline-red-500 divide-red-300' : 'bg-white hover:outline-blue-500 hover:bg-blue-50 divide-gray-200', 'col-span-1 divide-y rounded-lg shadow-lg hover:outline hover:outline-offset-2 border']">
                                        <div class="flex flex-col p-6">
                                            <h3 class="truncate text-sm font-medium text-gray-900">#: {{ type.id }}</h3>
                                            <h4 class="mt-1 truncate text-sm text-gray-500"><span class="text-gray-900">Name:</span> {{ type.name }}</h4>
                                            <p class="mt-1 truncate text-sm text-gray-500"><span class="text-gray-900">Description:</span> {{ type.description || 'No description' }}</p>
                                            <p class="mt-1 truncate text-sm text-gray-500"><span class="text-gray-900">Type:</span> {{ type.type }}</p>
                                        </div>
                                        <div>
                                            <div :class="[type.deleted_at ? 'divide-red-300' : 'divide-gray-200','-mt-px flex divide-x']">
                                                <div class="-ml-px flex w-0 flex-1">
                                                    <button v-if="type.deleted_at" @click="openEditModal(type)" class="bg-blue-50 px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-gray-300 hover:bg-blue-200 rounded-md">
                                                        Restore
                                                    </button>
                                                    <button v-if="!type.deleted_at" @click="openEditModal(type)" class="bg-blue-50 px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-gray-300 hover:bg-blue-200 rounded-md">
                                                        Edit
                                                    </button>
                                                    <button v-if="!type.deleted_at" @click="openDeleteModal(type)" class="ml-2 bg-red-50 px-3 py-2 text-sm font-semibold text-red-600 ring-1 ring-red-300 hover:bg-red-200 rounded-md">
                                                        Archive
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <pagination class="mt-6" :links="userEmergencyTypes.links" :results="userEmergencyTypes" :type="'userEmergencyTypes'" />
        </div>

         <!-- Add Option Modal (Used for Add & Edit) -->
         <div v-if="showOptionModal">
            <TransitionRoot as="template" :show="showOptionModal">
                <Dialog as="div" class="relative z-10" @close="closeOptionModal">
                    <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </TransitionChild>

                    <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                        <div class="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                                <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-6 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                                    <div class="text-center sm:text-left">
                                        <DialogTitle as="h3" class="text-lg font-semibold text-gray-900">
                                            {{ isEditing ? 'Edit Option' : 'Add New Option' }}
                                        </DialogTitle>
                                        <div class="mt-2">
                                            <label for="name" :class="errors.name ? 'block text-sm font-medium text-red-500' : 'block text-sm font-medium text-gray-700'">
                                                Name
                                            </label>
                                            <input id="name" type="text" v-model="optionForm.name"
                                                    :class="errors.name ? 'mt-1 w-full border-red-300 rounded-md shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm' : 'mt-1 w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm'">
                                                <p v-if="errors.name" class="text-red-500 text-sm">{{ errors.name }}</p>
                                        </div>
                                        <div class="mt-2">
                                            <label for="description" :class="errors.description ? 'block text-sm font-medium text-red-500' : 'block text-sm font-medium text-gray-700'">
                                                Description
                                            </label>
                                            <textarea id="description" v-model="optionForm.description" :class="errors.description ? 'mt-1 w-full border-red-300 rounded-md shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm' : 'mt-1 w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm'"></textarea>
                                                <p v-if="errors.description" class="text-red-500 text-sm">{{ errors.description }}</p>
                                        </div>
                                        <div class="mt-2">
                                            <label for="type" :class="errors.type ? 'block text-sm font-medium text-red-500' : 'block text-sm font-medium text-gray-700'">
                                                Type
                                            </label>
                                            <select id="type" v-model="optionForm.type" :class="errors.type ? 'mt-1 w-full border-red-300 rounded-md shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm' : 'mt-1 w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm'">
                                                <option value="emergency">Emergency</option>
                                                <option value="non_emergency">Non Emergency</option>
                                            </select>
                                                <p v-if="errors.type" class="text-red-500 text-sm">{{ errors.type }}</p>
                                        </div>
                                    </div>

                                    <div class="mt-5 sm:mt-6 flex">
                                        <button type="button" class="w-1/2 mr-1 justify-center rounded-md bg-gray-50 border-gray-400 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200 focus:outline-none" @click="closeOptionModal">
                                            Cancel
                                        </button>
                                        <button type="button" class="flex-shrink-0 w-1/2 ml-1 justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600" @click="saveOption">
                                            {{ isEditing ? 'Save' : 'Add Option' }}
                                        </button>
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </Dialog>
            </TransitionRoot>
        </div>

        <!-- Delete Confirmation Modal -->
        <div v-if="showDeleteModal">
            <TransitionRoot as="template" :show="showDeleteModal">
                <Dialog as="div" class="relative z-10" @close="showDeleteModal = false">
                    <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </TransitionChild>

                    <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                        <div class="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                                <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-6 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                                    <div class="text-center sm:text-left">
                                        <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                                            <font-awesome-icon :icon="faBoxArchive" class="h-6 w-6 text-red-500" aria-hidden="true" />
                                        </div>
                                        <div class="mt-2">
                                            <p class="text-sm text-center sm:mt-5">
                                                Are you sure you want to archive "<strong>{{ selectedOption?.name || 'this option' }}</strong>"?
                                            </p>
                                        </div>
                                    </div>

                                    <div class="mt-5 sm:mt-6 flex">
                                        <button type="button" class="w-1/2 mr-1 justify-center rounded-md bg-gray-50 border-gray-400 px-3 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-200 focus:outline-none" @click="showDeleteModal = false">
                                            Cancel
                                        </button>
                                        <button type="button" class="flex-shrink-0 w-1/2 ml-1 justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600" @click="deleteOption">
                                            Confirm
                                        </button>
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </Dialog>
            </TransitionRoot>
        </div>

        <!-- Restore  Modal  -->
        <div v-if="showRestoreModal">
            <TransitionRoot as="template" :show="showRestoreModal">
                <Dialog as="div" class="relative z-10" @close="showRestoreModal = false">
                    <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
                        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </TransitionChild>

                    <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
                        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                            <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                                <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6">
                                    <div>
                                        <div class="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                                            <font-awesome-icon :icon="faArrowsRotate" class="h-6 w-6 text-green-500" aria-hidden="true" />
                                        </div>
                                        <div class="mt-3 text-center sm:mt-5">
                                            <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">Are you sure you want to restore this option?</DialogTitle>
                                            <div class="mt-2">
                                            </div>
                                        </div>
                                    </div>
                                    <div class="mt-5 sm:mt-6 flex">
                                        <button type="button" class="flex-shrink-0 w-1/2 mr-1 justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600" @click="showRestoreModal = false">Close</button>
                                        <button type="button" class="flex-shrink-0 w-1/2 ml-1 justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600" @click="restoreOption()">Confirm</button>
                                    </div>
                                </DialogPanel>
                            </TransitionChild>
                        </div>
                    </div>
                </Dialog>
            </TransitionRoot>
        </div>

    </tailwind-layout>
</template>

<script>
import Icon from '@/Shared/Icon.vue';
import TailwindLayout from '@/Layouts/TailwindLayout.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faArrowsRotate, faBoxArchive, faChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination.vue'
import pickBy from 'lodash/pickBy'
import Button from "@/Jetstream/Button.vue";
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import { router } from '@inertiajs/vue3';

export default {
    props: {
        userEmergencyTypes: Object,
        filters: Object,
    },
    components: {
        Dialog,
        DialogPanel,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        FontAwesomeIcon,
        Button,
        Icon,
        Pagination,
        TailwindLayout
    },
    data() {
        return {
            form: {
                search: this.filters.search,
                trashed: this.filters.trashed,
                paginate: (this.filters.paginate)?this.filters.paginate:100,
                sort_by: this.filters.sort_by,
                sort_direction_icon : (this.filters.sort_direction === 'desc')?'sort-up':'sort-down'
            },
            showOptionModal: false,
            showDeleteModal: false,
            showRestoreModal: false,
            isEditing: false,
            selectedOption: null,
            optionForm: {
                id: null,
                name: '',
                description: '',
                type: '',
            },
            errors: {},
            faChevronLeft,
            faArrowsRotate,
        };
    },
    watch: {
        form: {
            deep: true,
            handler: throttle(function() {
                this.$inertia.get(this.route('maintenance.emergency-options.index'), pickBy(this.form), { preserveState: true })
            }, 150),
        },
    },
    methods: {
        fetchData(url = null) {
            this.$inertia.visit(url || this.route('maintenance.emergency-options.index'), {
                method: 'get',
                preserveState: false,
                replace: true,
                data: this.form,
            });
        },
        saveOption() {
            if (this.isEditing) {
                // Update Emergency Option
                this.$inertia.put(this.route('emergency-options.update', this.optionForm.id), this.optionForm, {
                    onSuccess: () => {
                        this.showOptionModal = false;
                        this.errors = {};
                    },
                    onError: (errors) => {
                        this.errors = errors;
                    }
                });
            } else {
                // Add Request Option
                this.$inertia.post(this.route('emergency-options.store'), this.optionForm, {
                    onSuccess: () => {
                        this.showOptionModal = false;
                        this.errors = {};
                    },
                    onError: (errors) => {
                        this.errors = errors;
                    }
                });
            }
        },
        openAddModal() {
            this.isEditing = false;
            this.optionForm = { id: null, name: '', description: '', type: '' };
            this.errors = {};
            this.showOptionModal = true;
        },
        openEditModal(option) {
            this.isEditing = true;
            this.optionForm = { id: option.id, name: option.name, description: option.description || '', type: option.type };
            this.errors = {};
            this.showOptionModal = true;
        },
        closeOptionModal() {
            this.showOptionModal = false;
            this.errors = {};
        },
        openDeleteModal(option) {
            this.selectedOption = option;
            this.showDeleteModal = true;
        },
        cancelEdit() {
            this.resetForm();
        },
        deleteOption(id) {
            if (!this.selectedOption) return;

            this.$inertia.delete(this.route('emergency-options.destroy', this.selectedOption.id), {
                onSuccess: () => {
                    this.showDeleteModal = false;
                    this.selectedOption = null;
                },
                onError: () => {
                    console.error("Failed to delete emergency option");
                }
            });
        },
        openRestoreModal(option) {
            this.selectedOption = option;
            this.showRestoreModal = true;
        },
        restoreOption() {
            if (!this.selectedOption) return;
            this.$inertia.put(this.route('emergency-options.restore', this.selectedOption.id), {}, {
                onSuccess: () => {
                    this.showRestoreModal = false;
                    this.selectedOption = null;
                },
                onError: () => {
                    console.error("Failed to restore emergency option");
                },
            });
        },
        editEmergency(type) {
            this.newEmergency.name = type.name;
            this.newEmergency.description = type.description || '';
            this.editEmergencyId = type.id;
            this.newEmergency.type = type.type;
            this.editing = true;
        },
        show_archived_options() {
            this.form.trashed = !this.form.trashed;
            this.fetchData();
        },
        sortTable(column) {
            this.form.sort_by = column
            this.form.sort_direction = (this.form.sort_direction === 'desc')?'asc':'desc'
            this.form.sort_direction_icon = (this.form.sort_direction === 'desc')?'sort-up':'sort-down'
        },
        reset() {
            this.form = mapValues(this.form, () => null)
        },
    }
};
</script>
